import type { CSSProperties } from 'react'
import { createContext, forwardRef, useContext } from 'react'

import { Toggle as TogglePrimitive } from '@base-ui/react/toggle'
import { ToggleGroup as ToggleGroupPrimitive } from '@base-ui/react/toggle-group'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'

const toggleVariants = tv({
  base: [
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium',
    'outline-none transition-colors',
    'hover:bg-surface-200 hover:text-surface-title-50',
    'focus-visible:ring-primary-200 focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'data-[pressed]:bg-surface-200 data-[pressed]:text-surface-title-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0'
  ],
  variants: {
    variant: {
      default: 'bg-transparent',
      outline: 'border-outline hover:bg-surface-200 border bg-transparent'
    },
    size: {
      sm: 'h-8 min-w-8 gap-1.5 px-2.5',
      md: 'h-9 min-w-9 gap-2 px-3',
      lg: 'h-10 min-w-10 gap-2 px-4'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'md'
  }
})

const toggleGroupItemVariants = tv({
  base: [
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium',
    'outline-none transition-colors',
    'text-surface-body',
    'hover:text-surface-title-50',
    'focus-visible:ring-primary-200 focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0'
  ],
  variants: {
    variant: {
      default:
        'bg-surface-200 border-surface-300 data-[pressed]:bg-surface data-[pressed]:text-surface-title-50 rounded-none border-r font-normal last:border-r-0 data-[pressed]:font-semibold',
      outline:
        'border-outline hover:bg-surface-200 data-[pressed]:bg-surface-200 data-[pressed]:text-surface-title-50 border bg-transparent'
    },
    size: {
      sm: 'h-6 px-4 text-xs',
      md: 'h-8 min-w-8 gap-2 px-3 text-sm',
      lg: 'h-9 min-w-9 gap-2 px-4 text-sm'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'md'
  }
})

export type ToggleVariant = NonNullable<
  VariantProps<typeof toggleVariants>['variant']
>

export type ToggleSize = NonNullable<
  VariantProps<typeof toggleVariants>['size']
>

export type ToggleProps = React.ComponentProps<typeof TogglePrimitive> &
  VariantProps<typeof toggleVariants> & {
    className?: string
  }

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  ({ variant = 'default', size = 'md', className, ...props }, ref) => (
    <TogglePrimitive
      ref={ref}
      data-slot='toggle'
      data-variant={variant}
      data-size={size}
      className={cn(toggleVariants({ variant, size }), className)}
      {...props}
    />
  )
)
Toggle.displayName = 'Toggle'

const ToggleGroupContext = createContext<{
  variant?: 'default' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  spacing?: number
}>({})

export type ToggleGroupProps = React.ComponentProps<
  typeof ToggleGroupPrimitive
> & {
  variant?: 'default' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  spacing?: number
  className?: string
}

export const ToggleGroup = forwardRef<HTMLDivElement, ToggleGroupProps>(
  (
    {
      variant = 'default',
      size = 'md',
      spacing = 0,
      className,
      children,
      ...props
    },
    ref
  ) => (
    <ToggleGroupContext.Provider value={{ variant, size, spacing }}>
      <ToggleGroupPrimitive
        ref={ref}
        data-slot='toggle-group'
        data-variant={variant}
        data-spacing={spacing}
        style={{ '--toggle-spacing': spacing } as CSSProperties}
        className={cn(
          'group/toggle-group inline-flex items-center',
          variant === 'default' &&
            spacing === 0 &&
            'bg-surface border-surface-300 overflow-hidden rounded-lg border',
          spacing > 0 ? 'gap-[calc(var(--toggle-spacing)*0.25rem)]' : 'gap-0',
          className
        )}
        {...props}
      >
        {children}
      </ToggleGroupPrimitive>
    </ToggleGroupContext.Provider>
  )
)
ToggleGroup.displayName = 'ToggleGroup'

export type ToggleGroupItemProps = React.ComponentProps<
  typeof TogglePrimitive
> & {
  variant?: 'default' | 'outline'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export const ToggleGroupItem = forwardRef<
  HTMLButtonElement,
  ToggleGroupItemProps
>(({ variant, size, className, ...props }, ref) => {
  const context = useContext(ToggleGroupContext)
  const resolvedVariant = variant ?? context.variant ?? 'default'
  const resolvedSize = size ?? context.size ?? 'md'

  return (
    <TogglePrimitive
      ref={ref}
      data-slot='toggle-group-item'
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-spacing={context.spacing}
      className={cn(
        toggleGroupItemVariants({
          variant: resolvedVariant,
          size: resolvedSize
        }),
        // collapse borders for outline variant when spacing is 0
        resolvedVariant === 'outline' &&
          context.spacing === 0 &&
          'group-data-[variant=outline]/toggle-group:not(:first-child):border-l-0 group-data-[variant=outline]/toggle-group:not(:first-child):not(:last-child):rounded-none group-data-[variant=outline]/toggle-group:first:rounded-l-md group-data-[variant=outline]/toggle-group:last:rounded-r-md',
        className
      )}
      {...props}
    />
  )
})
ToggleGroupItem.displayName = 'ToggleGroupItem'
